import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useFetch, fmtDate, CHART_COLORS } from "@/lib/shieldx";
import { PageHeader, Panel, Loader, Stat } from "@/components/shieldx/Primitives";
import { RiskBadge, DemoTag } from "@/components/shieldx/Badges";
import SecurityTopology from "@/components/shieldx/SecurityTopology";
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { LayoutDashboard, Building2, Boxes, ShieldAlert, Ban, Bot, Siren, Bell, Plug, ShieldCheck } from "lucide-react";

const ttStyle = { background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12, fontFamily: "JetBrains Mono" };

export default function CommandCenter() {
  const { data, loading } = useFetch("/dashboard/command-center");
  if (loading || !data) return <Loader label="Loading command center" />;
  const k = data.kpis;

  const kpis = [
    { label: "Organizations", value: k.organizations, icon: Building2, tone: "slate" },
    { label: "Protected Assets", value: `${k.protected_assets}/${k.total_assets}`, icon: Boxes, tone: "blue" },
    { label: "Active Threats", value: k.active_threats, icon: ShieldAlert, tone: "amber" },
    { label: "Blocked Attacks", value: k.blocked_attacks, icon: Ban, tone: "emerald" },
    { label: "AI-Related Threats", value: k.ai_threats, icon: Bot, tone: "cyan" },
    { label: "Critical Incidents", value: k.critical_incidents, icon: Siren, tone: "red" },
    { label: "Security Alerts", value: k.security_alerts, icon: Bell, tone: "amber" },
    { label: "AI Agents", value: k.ai_agents, icon: Bot, tone: "purple" },
  ];

  return (
    <div>
      <PageHeader icon={LayoutDashboard} title="Command Center"
        subtitle="Global protection posture across all registered organizations and assets."
        actions={<DemoTag />} />

      <SecurityTopology kpis={k} />

      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {kpis.map((s) => (
          <div key={s.label} className="relative rounded-xl border border-slate-800 bg-slate-900/50 p-4">
            <div className="flex items-start justify-between">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{s.label}</p>
              <s.icon className="h-4 w-4 text-slate-600" />
            </div>
            <p className={`mt-2 font-display text-2xl font-bold tabular-nums text-${s.tone === "slate" ? "slate-50" : s.tone + "-400"}`}
               style={{ color: { slate: "#f8fafc", blue: "#60a5fa", amber: "#fbbf24", emerald: "#34d399", cyan: "#67e8f9", red: "#f87171", purple: "#c4b5fd" }[s.tone] }}>
              {s.value}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Stat label="Protection Coverage" value={`${k.protection_coverage}%`} tone="blue" sub="Assets actively protected or monitored" />
        <Stat label="Integration Health" value={`${k.integration_health}%`} tone="emerald" sub="Connected & verified integrations" />
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Current Risk</p>
          <div className="mt-2.5"><RiskBadge value={k.current_risk} className="text-sm" /></div>
          <p className="mt-2 text-xs text-slate-500">Weighted across all asset risk levels</p>
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Panel title="Threat Trend" desc="Detected vs blocked — last 7 days" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.threat_trend}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity={0.4} /><stop offset="100%" stopColor="#2563EB" stopOpacity={0} /></linearGradient>
                <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis dataKey="day" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={ttStyle} />
              <Area type="monotone" dataKey="threats" stroke="#2563EB" strokeWidth={2} fill="url(#g1)" name="Detected" />
              <Area type="monotone" dataKey="blocked" stroke="#10B981" strokeWidth={2} fill="url(#g2)" name="Blocked" />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Top Threat Categories" desc="Across all assets">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data.top_categories} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                {data.top_categories.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="#0f172a" />)}
              </Pie>
              <Tooltip contentStyle={ttStyle} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Panel title="Recent Activity" desc="From the tamper-evident audit ledger">
            <div className="space-y-1">
              {data.recent_activity.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 hover:bg-slate-800/40">
                  <div className="flex items-center gap-3">
                    <span className="rounded border border-slate-700 bg-slate-800/60 px-2 py-0.5 font-mono text-[10px] text-slate-300">{a.action}</span>
                    <span className="text-sm text-slate-300">{a.target}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span className="font-mono">{a.actor}</span>
                    <span>{fmtDate(a.timestamp)}</span>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
        <LiveStream />
      </div>
    </div>
  );
}

const STREAM_EVENTS = [
  ["Suspicious authentication activity detected", "amber"],
  ["Policy evaluation initiated", "slate"],
  ["Request denied — outside approved scope", "red"],
  ["Rate limit applied to source", "amber"],
  ["Incident created and owner notified", "cyan"],
  ["AI agent self-escalation blocked", "purple"],
  ["Integration marked UNTRUSTED pending review", "amber"],
  ["Source blocked, sessions revoked", "red"],
  ["Recovery point integrity verified", "emerald"],
  ["Asset isolated — lateral movement prevented", "red"],
];
const DOT = { amber: "#fbbf24", red: "#f87171", slate: "#94a3b8", cyan: "#67e8f9", purple: "#c4b5fd", emerald: "#34d399" };

function LiveStream() {
  const [events, setEvents] = useState([]);
  const i = useRef(0);
  useEffect(() => {
    const push = () => {
      const [msg, tone] = STREAM_EVENTS[i.current % STREAM_EVENTS.length];
      i.current += 1;
      const ts = new Date().toLocaleTimeString("en-GB");
      setEvents((e) => [{ ts, msg, tone, id: `${Date.now()}-${i.current}` }, ...e].slice(0, 9));
    };
    push();
    const t = setInterval(push, 2600);
    return () => clearInterval(t);
  }, []);
  return (
    <Panel title="Live Event Stream" desc="DEMO / SIMULATED">
      <div className="space-y-1.5" data-testid="live-event-stream">
        <AnimatePresence initial={false}>
          {events.map((e) => (
            <motion.div key={e.id} layout
              initial={{ opacity: 0, x: -16, height: 0 }}
              animate={{ opacity: 1, x: 0, height: "auto" }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="flex items-start gap-2.5 rounded-md px-2 py-1.5">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: DOT[e.tone], boxShadow: `0 0 6px ${DOT[e.tone]}` }} />
              <div className="min-w-0">
                <p className="truncate text-xs text-slate-300">{e.msg}</p>
                <p className="font-mono text-[10px] text-slate-600">{e.ts}</p>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </Panel>
  );
}
